import { X } from 'lucide-react';
import React, { type ReactNode } from 'react';
import { ModalOverlay as RACModalOverlay, type ModalOverlayProps, Modal as RACModal, composeRenderProps } from 'react-aria-components';
import { ErrorBoundary } from 'react-error-boundary';
import { tv, type VariantProps } from 'tailwind-variants';
import { cn } from '../utils';
import { Header3 } from '../text/Header3';
import { StatusLabel } from '../StatusLabel';
import { Prompt } from '../Prompt';
import { Loading } from '../../Loading';

export const overlayStyles = tv({
	base: 'fixed inset-0 z-20 bg-black/50',
	variants: {
		isEntering: {
			true: 'duration-200 ease-out animate-in fade-in'
		},
		isExiting: {
			true: 'duration-200 ease-in animate-out fade-out'
		}
	}
});

export const modalStyles = tv({
	base: 'relative flex flex-col overflow-hidden rounded-2xl border border-black/10 bg-white bg-clip-padding text-left align-middle font-sans text-slate-700 shadow-2xl forced-colors:bg-[Canvas]',
	variants: {
		size: {
			xl: 'size-full',
			lg: 'max-h-[92vh] w-full max-w-5xl',
			md: 'max-h-[80vh] w-full max-w-[50%]'
		},
		isEntering: {
			true: 'duration-200 ease-out animate-in zoom-in-105'
		},
		isExiting: {
			true: 'duration-200 ease-in animate-out zoom-out-95'
		}
	},
	defaultVariants: {
		size: 'md'
	}
});

type ModalVariants = VariantProps<typeof modalStyles>;

export function ModalOverlay(props: ModalProps) {
	return (
		<RACModalOverlay
			{...props}
			isOpen
			className={composeRenderProps(props.className, (className, renderProps) => cn(overlayStyles(renderProps), className))}
		/>
	);
}

const modalHeaderStyles = tv({
	base: 'flex items-center justify-between border-b border-gray-200 px-6 py-4'
});

function CloseModalButton({ closeModal }: { closeModal: () => void }) {
	return (
		<button type="button" onClick={closeModal} className="rounded-full p-1 hover:bg-gray-100" aria-label="Close modal">
			<X className="size-5 text-gray-500" />
		</button>
	);
}

interface ModalHeaderProps {
	closeModal?: () => void;
	title: string | React.ReactNode;
	TitleIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
	status?: string | React.ReactNode;
	actions?: React.ReactNode;
	titleClassName?: string;
}
function ModalHeader({ closeModal, title, TitleIcon, status, actions, titleClassName }: ModalHeaderProps) {
	const titleLabel = typeof title === 'string' ? <Header3>{title}</Header3> : title;
	const headerStyles = modalHeaderStyles();
	return (
		<div className={cn(headerStyles, titleClassName)} slot="title">
			<div className="flex items-center gap-4">
				{!TitleIcon ? (
					titleLabel
				) : (
					<div className="flex items-center gap-2">
						<TitleIcon className="size-5 text-primary-500" />
						{titleLabel}
					</div>
				)}
				{status ? typeof status === 'string' ? <StatusLabel status={status} /> : status : null}
			</div>
			<div className="flex items-center gap-1.5">
				{actions}
				{closeModal && <CloseModalButton closeModal={closeModal} />}
			</div>
		</div>
	);
}

export function CustomModalHeader({
	closeModal,
	titleClassName,
	children
}: {
	closeModal: () => void;
	titleClassName?: string;
	children: (closeModal: ReactNode) => ReactNode;
}) {
	const headerStyles = modalHeaderStyles();
	return (
		<div className={cn(headerStyles, titleClassName)} slot="title">
			{children(<CloseModalButton closeModal={closeModal} />)}
		</div>
	);
}

function ModalErrorPrompt({ resetErrorBoundary, closeModal }: { resetErrorBoundary: () => void; closeModal?: () => void }) {
	return (
		<Prompt
			intent="confirm"
			title="Something went wrong"
			text="An unexpected error occurred. Please try again."
			onAction={closeModal ?? resetErrorBoundary}
		/>
	);
}

function LoadingOverlay() {
	return (
		<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
			<Loading />
		</div>
	);
}

export function ModalWrapper(props: ModalProps) {
	const children = (
		<ErrorBoundary
			fallbackRender={({ resetErrorBoundary }) => <ModalErrorPrompt resetErrorBoundary={resetErrorBoundary} closeModal={props.closeModal} />}
		>
			<React.Suspense fallback={<LoadingOverlay />}>{props.children}</React.Suspense>
		</ErrorBoundary>
	);
	return (
		<div className={props.size !== 'xl' ? 'fixed inset-0 z-50 flex items-center justify-center p-4' : 'absolute inset-8 overflow-hidden'}>
			<RACModal
				{...props}
				className={composeRenderProps(props.className, (className, renderProps) =>
					cn(modalStyles({ ...renderProps, size: props.size }), className)
				)}
			>
				{props.headerSlot ? props.headerSlot : props.title && <ModalHeader {...props} title={props.title!} />}
				{props.size !== 'xl' ? (
					<div className="flex flex-1 flex-col space-y-5 overflow-y-auto py-5">
						<div className="px-6">{children}</div>
					</div>
				) : (
					<div className="min-h-0 flex-1 overflow-y-auto px-8 py-4">{children}</div>
				)}

				{props.footer && <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3.5">{props.footer}</div>}
				{props.isPending && <LoadingOverlay />}
			</RACModal>
		</div>
	);
}

export interface ModalProps extends Omit<ModalOverlayProps, 'children'>, Omit<ModalHeaderProps, 'title'> {
	children: React.ReactNode;
	headerSlot?: React.ReactNode;
	title?: string | React.ReactNode;
	titleClassName?: string;
	footer?: React.ReactNode;
	size?: ModalVariants['size'];
	isPending?: boolean;
	modalClassName?: string;
}

export function Modal(props: ModalProps) {
	return (
		<ModalOverlay {...props}>
			<ModalWrapper {...props} modalClassName={props.modalClassName} />
		</ModalOverlay>
	);
}
